/**
 * BoltPlanningCard — Structured planning questions from BOLT before mutations
 *
 * Follows BoltDisambiguationCard pattern: renders inside the assistant message
 * body as an interactive card. Uses orange accent + purple left rail.
 *
 * Three-step mutation flow:
 *   1. PLANNING — Claude calls planning_questions tool → this card renders
 *   2. ANSWERS — User fills in and submits → sent as next chat turn
 *   3. CONFIRM — Claude previews mutation → user confirms
 */

import React, { useState, useCallback } from 'react';
import BoltPlanningInput from './BoltPlanningInput';

const CONTEXT_LABELS = {
  task_creation: 'Creating a task',
  event_filing: 'Filing a safety event',
  scheduling: 'Scheduling an event',
  progress_update: 'Updating progress',
  hazard_report: 'Filing a hazard report',
  training_assignment: 'Assigning training',
  escalation: 'Escalating an issue',
};

export default function BoltPlanningCard({
  planId,
  context,
  questions,
  onSubmit,
  onSkip,
  disabled = false,
  planningSessionId,
}) {
  const [answers, setAnswers] = useState({});

  // Callback ref: focus the first focusable element inside the first question
  const firstQuestionRef = useCallback(
    (node) => {
      if (node && !disabled) {
        const focusable = node.querySelector('input, button');
        focusable?.focus();
      }
    },
    [disabled]
  );

  const handleChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = () => {
    const answerArray = questions.map((q) => ({
      id: q.id,
      value: answers[q.id] || '',
    }));
    if (planningSessionId) {
      onSubmit({ planId, answers: answerArray, planningSessionId });
    } else {
      onSubmit(planId, answerArray);
    }
  };

  const allAnswered =
    questions.length > 0 &&
    questions.every((q) => {
      const val = answers[q.id];
      return val !== undefined && val !== '';
    });

  if (!questions || questions.length === 0) {
    return null;
  }

  return (
    <div
      className={`bolt-planning-card${disabled ? ' bolt-planning-card--submitted' : ''}`}
      role="region"
      aria-label="Planning questions"
    >
      <div className="bolt-planning-card__header">
        <div className="bolt-planning-card__rail" />
        <div className="bolt-planning-card__title">
          {CONTEXT_LABELS[context] || 'Planning'} — {questions.length}{' '}
          {questions.length === 1 ? 'question' : 'questions'}
        </div>
      </div>

      <div className="bolt-planning-card__questions">
        {questions.map((q, index) => (
          <div
            key={q.id}
            className="bolt-planning-card__question"
            ref={index === 0 ? firstQuestionRef : undefined}
          >
            <label className="bolt-planning-card__label" htmlFor={`bolt-plan-${planId}-${q.id}`}>
              <span className="bolt-planning-card__number">{index + 1}.</span>
              {q.text}
            </label>
            <BoltPlanningInput
              id={`bolt-plan-${planId}-${q.id}`}
              question={q}
              value={answers[q.id] || ''}
              onChange={(val) => handleChange(q.id, val)}
              disabled={disabled}
            />
          </div>
        ))}
      </div>

      <div className="bolt-planning-card__actions">
        <button
          className="bolt-planning-card__submit"
          onClick={handleSubmit}
          disabled={disabled || !allAnswered}
          type="button"
        >
          Submit Answers
        </button>
        {onSkip && !disabled && (
          <button
            className="bolt-planning-card__skip"
            onClick={onSkip}
            type="button"
          >
            Skip and type instead
          </button>
        )}
      </div>
    </div>
  );
}
